/**
 * 设备详情 —— 设备概览 / 设备密钥 / 仪器激活码 / 维护动态密码。
 *
 * 各标签页各自独立取数：
 *   - 设备概览：复用父级的 `getDevice` 结果，任何变更后调用 `onChanged()` 重新拉取；
 *   - 设备密钥：签发/查看/轮换/吊销/下发信封，设备密钥明文只在响应中出现；
 *   - 仪器激活码：按阶段签发 + 独立校验 + 历史记录；
 *   - 维护动态密码：进入标签页时取一次，随后按 60 秒窗口自动刷新。
 *
 * 注意：`查看设备密钥明文` 与 `维护动态密码` 每次调用都会在服务端留下操作记录，
 * 因此维护动态密码只在标签页处于打开状态时才轮询。
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Ban,
  Check,
  CircleAlert,
  CircleCheck,
  Copy,
  Eye,
  Info,
  KeyRound,
  Loader2,
  Lock,
  Radio,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
  ShieldCheck,
  Timer,
  TriangleAlert,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  api,
  useAsync,
  useTicker,
  type ActivationIssue,
  type Device,
  type DeviceDetail,
  type DeviceStatus,
  type DynamicPassword,
  type LicenseCodeRow,
} from '@/lib/api'
import { cn, formatActivationCode, formatDynamicCode, formatTime, relativeTime } from '@/lib/utils'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

// ───────────────────────────── 常量与工具 ─────────────────────────────

type Stage = 1 | 2 | 3

const STAGES: readonly Stage[] = [1, 2, 3] as const

const STAGE_LABEL: Record<Stage, string> = { 1: '阶段一', 2: '阶段二', 3: '阶段三' }

const STATUS_META: Record<
  DeviceStatus,
  { label: string; variant: 'warning' | 'success' | 'destructive' | 'outline' }
> = {
  pending: { label: '待批准', variant: 'warning' },
  active: { label: '已激活', variant: 'success' },
  suspended: { label: '已停用', variant: 'destructive' },
  retired: { label: '已退役', variant: 'outline' },
}

const NOTICE_CLASS =
  'flex items-start gap-3 rounded-md border border-[color-mix(in_oklch,var(--color-warning)_40%,transparent)] bg-[color-mix(in_oklch,var(--color-warning)_10%,transparent)] px-4 py-3 text-sm'

const TONE_CLASS: Record<'default' | 'warning' | 'success', string> = {
  default: 'border-[var(--color-border)] bg-[var(--color-card)]',
  warning:
    'border-[color-mix(in_oklch,var(--color-warning)_40%,transparent)] bg-[color-mix(in_oklch,var(--color-warning)_10%,transparent)]',
  success:
    'border-[color-mix(in_oklch,var(--color-success)_40%,transparent)] bg-[color-mix(in_oklch,var(--color-success)_10%,transparent)]',
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

/** 复制到剪贴板并提示；不支持的浏览器降级为提示手动复制。 */
async function copyText(text: string, message = '已复制到剪贴板'): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(message)
  } catch {
    toast.error('复制失败，请手动选择文本复制')
  }
}

/** 把毫秒时长转成人话（天/小时/分钟）。 */
function humanizeDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  if (days > 0) return `${days} 天 ${hours} 小时`
  if (hours > 0) return `${hours} 小时 ${minutes} 分钟`
  return `${minutes} 分钟`
}

/** 定义列表条目（概览页用）。 */
/** 复制按钮：带复制成功反馈，供上位机授权码/设备密钥/仪器激活码等长文本复用。 */
function CopyButton({
  value,
  label = '复制',
  size = 'default',
}: {
  value: string
  label?: string
  size?: 'default' | 'sm'
}) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      toast.success('已复制到剪贴板')
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('复制失败，请手动选择文本')
    }
  }

  return (
    <Button variant="outline" size={size} onClick={copy}>
      {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      {copied ? '已复制' : label}
    </Button>
  )
}

function DefinitionItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <dt className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
        {label}
      </dt>
      <dd className="break-all text-sm">{children}</dd>
    </div>
  )
}

/** 等宽文本块 + 复制按钮（设备密钥明文、下发信封等）。 */
function CopyableBlock({
  title,
  hint,
  value,
  tone = 'default',
  action,
}: {
  title: string
  hint?: string
  value: string
  tone?: 'default' | 'warning' | 'success'
  action?: ReactNode
}) {
  return (
    <div className={cn('rounded-md border px-4 py-3', TONE_CLASS[tone])}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium">{title}</div>
          {hint ? (
            <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">{hint}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {action}
          <Button variant="outline" size="sm" onClick={() => void copyText(value)}>
            <Copy className="size-3.5" />
            复制
          </Button>
        </div>
      </div>
      <div className="font-mono-token mt-3 max-h-56 overflow-auto break-all rounded bg-[var(--color-muted)] px-3 py-2 text-xs leading-relaxed">
        {value}
      </div>
    </div>
  )
}

/** 二次确认对话框（吊销设备密钥、轮换、停用等破坏性操作）。 */
// ───────────────────────────── 概览 ─────────────────────────────

/**
 * 注册档案页签。
 *
 * 展示设备 App「注册页」上由用户填写的表单（单位名称 / 管理员姓名 / 联系电话 / 科室），
 * 并允许运维修正现场填错的内容。表单由设备通过 `POST /api/device/register` 上报，
 * 首次上报会自动以 pending 建档。
 */
/**
 * 计算 PEM 公钥的 SHA-256 指纹（前 16 位十六进制），与服务端审计口径一致。
 *
 * `crypto.subtle` 只在安全上下文可用（https 或 localhost）。内网以
 * `http://<IP>:8787` 访问时会拿不到，此时返回 null，界面退化为不显示指纹 ——
 * 指纹只是便于人工核对，不该因为它让整页出问题。
 */
async function pemFingerprint(pem: string): Promise<string | null> {
  try {
    if (!globalThis.crypto?.subtle) return null
    const b64 = pem.replace(/-----(BEGIN|END)[^-]+-----/g, '').replace(/\s+/g, '')
    const der = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
    const digest = await crypto.subtle.digest('SHA-256', der)
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 16)
  } catch {
    return null
  }
}

/** 公钥 PEM 的粗校验：只认 SPKI 公钥头。 */
function looksLikePublicKeyPem(pem: string): boolean {
  return /-----BEGIN PUBLIC KEY-----/.test(pem) && /-----END PUBLIC KEY-----/.test(pem)
}

/**
 * 设备公钥：设备密钥下发信封的收件地址。
 *
 * 公钥由设备在注册上报时自动提交，也可由运维在此手动补录或替换。
 * 替换/清除会影响此后生成的密钥下发信封，因此保存前二次确认。
 */
function PublicKeyCard({ device, onChanged }: { device: Device; onChanged: () => void }) {
  const [publicKeyPem, setPublicKeyPem] = useState(device.public_key_pem ?? '')
  const [keySaving, setKeySaving] = useState(false)
  const [pendingKeyChange, setPendingKeyChange] = useState<string | null>(null)
  const [fingerprint, setFingerprint] = useState<string | null>(null)

  useEffect(() => {
    setPublicKeyPem(device.public_key_pem ?? '')
  }, [device.public_key_pem])

  useEffect(() => {
    let alive = true
    if (!device.public_key_pem) {
      setFingerprint(null)
      return
    }
    void pemFingerprint(device.public_key_pem).then((fp) => {
      if (alive) setFingerprint(fp)
    })
    return () => {
      alive = false
    }
  }, [device.public_key_pem])

  const keyDirty = publicKeyPem.trim() !== (device.public_key_pem ?? '').trim()
  const keyFormatOk = publicKeyPem.trim() === '' || looksLikePublicKeyPem(publicKeyPem)

  /** 替换公钥会让此前下发的信封无法被设备解开，必须二次确认。 */
  function requestKeySave() {
    if (!keyFormatOk) {
      toast.error('公钥格式不正确（应为 -----BEGIN PUBLIC KEY----- 开头的 PEM）')
      return
    }
    setPendingKeyChange(publicKeyPem.trim())
  }

  async function commitKeySave() {
    if (pendingKeyChange === null) return
    setKeySaving(true)
    try {
      await api.updateDevice(device.device_id, { publicKeyPem: pendingKeyChange })
      toast.success(pendingKeyChange ? '公钥已保存' : '公钥已清除')
      setPendingKeyChange(null)
      onChanged()
    } catch (err) {
      toast.error(errorMessage(err, '保存公钥失败'))
    } finally {
      setKeySaving(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>设备公钥</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="text-[var(--color-muted-foreground)]">当前状态</span>
            {device.public_key_pem ? (
              <Badge variant="success">已登记</Badge>
            ) : (
              <Badge variant="destructive">未登记（下发设备密钥会失败）</Badge>
            )}
            {fingerprint ? (
              <span className="font-mono-token text-[var(--color-muted-foreground)]">
                指纹 {fingerprint}
              </span>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="device-public-key">公钥 PEM</Label>
            <textarea
              id="device-public-key"
              value={publicKeyPem}
              onChange={(e) => setPublicKeyPem(e.target.value)}
              rows={6}
              spellCheck={false}
              placeholder={'-----BEGIN PUBLIC KEY-----\n…\n-----END PUBLIC KEY-----'}
              className="font-mono-token w-full rounded-md border border-[var(--color-input)] bg-transparent px-3 py-2 text-xs leading-relaxed shadow-sm placeholder:text-[var(--color-muted-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
            />
            {!keyFormatOk && (
              <p className="text-[11px] text-[var(--color-destructive)]">
                格式不正确：应为包含 BEGIN/END PUBLIC KEY 的 PEM 文本。
              </p>
            )}
            <p className="text-[11px] text-[var(--color-muted-foreground)]">
              留空保存即清除公钥。设备注册上报时会自动提交公钥，也可在此手动补录。
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={requestKeySave} disabled={keySaving || !keyDirty || !keyFormatOk}>
              {keySaving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              保存公钥
            </Button>
            {keyDirty ? (
              <span className="text-xs text-[var(--color-warning)]">有未保存的修改</span>
            ) : (
              <span className="text-xs text-[var(--color-muted-foreground)]">无变化</span>
            )}
          </div>
        </CardContent>
      </Card>

      {pendingKeyChange !== null && (
        <ConfirmDialog
          title={pendingKeyChange ? '替换设备公钥？' : '清除设备公钥？'}
          description={
            pendingKeyChange
              ? '公钥是设备密钥下发信封的收件地址。替换后，此前用旧公钥加密的信封将无法被该设备解开，需要重新下发设备密钥。'
              : '清除后，该设备将无法接收设备密钥下发信封（接口返回 409），直到重新上报或补录公钥。'
          }
          confirmLabel={pendingKeyChange ? '确认替换' : '确认清除'}
          danger
          busy={keySaving}
          onCancel={() => setPendingKeyChange(null)}
          onConfirm={() => void commitKeySave()}
        />
      )}
    </>
  )
}

function RegistrationTab({ device, onChanged }: { device: Device; onChanged: () => void }) {
  const [companyName, setCompanyName] = useState(device.company_name ?? '')
  const [contactName, setContactName] = useState(device.contact_name ?? '')
  const [contactPhone, setContactPhone] = useState(device.contact_phone ?? '')
  const [department, setDepartment] = useState(device.department ?? '')
  const [model, setModel] = useState(device.model ?? '')
  const [serialNumber, setSerialNumber] = useState(device.serial_number ?? '')
  const [androidId, setAndroidId] = useState(device.android_id ?? '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setCompanyName(device.company_name ?? '')
    setContactName(device.contact_name ?? '')
    setContactPhone(device.contact_phone ?? '')
    setDepartment(device.department ?? '')
    setModel(device.model ?? '')
    setSerialNumber(device.serial_number ?? '')
    setAndroidId(device.android_id ?? '')
  }, [
    device.company_name,
    device.contact_name,
    device.contact_phone,
    device.department,
    device.model,
    device.serial_number,
    device.android_id,
  ])

  const dirty =
    companyName !== (device.company_name ?? '') ||
    contactName !== (device.contact_name ?? '') ||
    contactPhone !== (device.contact_phone ?? '') ||
    department !== (device.department ?? '') ||
    model !== (device.model ?? '') ||
    serialNumber !== (device.serial_number ?? '') ||
    androidId !== (device.android_id ?? '')

  const reported = device.form_reported_at !== null && device.form_reported_at !== undefined

  async function save() {
    setSaving(true)
    try {
      await api.updateDevice(device.device_id, {
        companyName,
        contactName,
        contactPhone,
        department,
        model,
        serialNumber,
        androidId,
      })
      toast.success('注册档案已保存')
      onChanged()
    } catch (err) {
      toast.error(errorMessage(err, '保存注册档案失败'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className={reported ? TONE_CLASS.success : TONE_CLASS.warning}>
        <div className="flex items-start gap-3 px-4 py-3 text-sm">
          <Info className="mt-0.5 size-4 shrink-0 text-[var(--color-muted-foreground)]" />
          <div>
            <div className="font-medium">
              {reported ? '设备已上报注册档案' : '设备尚未上报注册档案'}
            </div>
            <p className="mt-1 text-[var(--color-muted-foreground)]">
              {reported ? (
                <>
                  上报时间：{formatTime(device.form_reported_at)}
                  。以下内容可在管理端修正，修正不会被设备覆盖（重复上报只更新设备自己提交的字段）。
                </>
              ) : (
                <>
                  用户在仪器「设备注册」页填写后，App 会通过{' '}
                  <code className="font-mono-token">POST /api/device/register</code> 上报。
                  若设备端未配置管理端地址，可在此手动补录。
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>注册表单</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="reg-company">单位名称</Label>
              <Input
                id="reg-company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="如：东晟分析仪器有限公司"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-contact">管理员姓名</Label>
              <Input
                id="reg-contact"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="如：张工"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-phone">联系电话</Label>
              <Input
                id="reg-phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="手机或带区号座机"
              />
              <p className="text-[11px] text-[var(--color-muted-foreground)]">
                仪器端校验规则：11 位手机号，或「区号-号码」形式的座机。
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-dept">科室</Label>
              <Input
                id="reg-dept"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="如：理化检验科"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-model">型号</Label>
              <Input
                id="reg-model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="如：RZ-104"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-serial">设备序列号</Label>
              <Input
                id="reg-serial"
                className="font-mono-token"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="仪器硬件 SN"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-android-id">Android ID</Label>
              <Input
                id="reg-android-id"
                className="font-mono-token"
                value={androidId}
                onChange={(e) => setAndroidId(e.target.value)}
                placeholder="设备 Android ID"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving || !dirty}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              保存修改
            </Button>
            {dirty ? (
              <span className="text-xs text-[var(--color-warning)]">有未保存的修改</span>
            ) : (
              <span className="text-xs text-[var(--color-muted-foreground)]">无变化</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function OverviewTab({
  device,
  stageState,
  onChanged,
}: {
  device: Device
  stageState: DeviceDetail['stageState']
  onChanged: () => void
}) {
  const [statusBusy, setStatusBusy] = useState<DeviceStatus | null>(null)
  const [lockBusy, setLockBusy] = useState(false)
  const [lockOverride, setLockOverride] = useState<boolean | null>(null)
  const [confirmRetire, setConfirmRetire] = useState(false)

  // 重新拉取到新的设备对象后，交回服务端真值。
  useEffect(() => {
    setLockOverride(null)
  }, [device])

  const meta = STATUS_META[device.status]
  const lockChecked = lockOverride ?? device.stage_lock_enabled === 1

  async function applyStatus(next: DeviceStatus) {
    setStatusBusy(next)
    try {
      await api.setStatus(device.device_id, next)
      toast.success(`状态已更新为「${STATUS_META[next].label}」`)
      setConfirmRetire(false)
      onChanged()
    } catch (err) {
      toast.error(errorMessage(err, '状态更新失败'))
    } finally {
      setStatusBusy(null)
    }
  }

  async function toggleLock(next: boolean) {
    setLockOverride(next)
    setLockBusy(true)
    try {
      await api.updateDevice(device.device_id, { stageLockEnabled: next })
      toast.success(next ? '已开启阶段锁' : '已关闭阶段锁（设备端将不再要求仪器激活码）')
      onChanged()
    } catch (err) {
      setLockOverride(null)
      toast.error(errorMessage(err, '阶段锁更新失败'))
    } finally {
      setLockBusy(false)
    }
  }

  const stageFlags: Record<Stage, boolean> = {
    1: stageState?.stage1_activated === 1,
    2: stageState?.stage2_activated === 1,
    3: stageState?.stage3_activated === 1,
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <div>
            <CardTitle>注册档案</CardTitle>
          </div>
          <Badge variant={meta.variant}>{meta.label}</Badge>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            <DefinitionItem label="设备 ID">
              <span className="font-mono-token">{device.device_id}</span>
            </DefinitionItem>
            <DefinitionItem label="设备名称">{device.device_name}</DefinitionItem>
            <DefinitionItem label="设备类型">{device.device_type}</DefinitionItem>
            <DefinitionItem label="型号">{device.model || '—'}</DefinitionItem>
            <DefinitionItem label="设备序列号">
              <span className="font-mono-token">{device.serial_number || '—'}</span>
            </DefinitionItem>
            <DefinitionItem label="Android ID">
              <span className="font-mono-token">{device.android_id || '—'}</span>
            </DefinitionItem>
            <DefinitionItem label="App 版本">{device.app_version || '—'}</DefinitionItem>
            <DefinitionItem label="上位机机器码">
              <span className="font-mono-token">{device.machine_code || '—'}</span>
            </DefinitionItem>
            <DefinitionItem label="注册时间">{formatTime(device.registered_at)}</DefinitionItem>
            <DefinitionItem label="批准时间">
              {device.approved_at ? formatTime(device.approved_at) : '—'}
              {device.approved_by ? (
                <span className="text-[var(--color-muted-foreground)]">
                  （{device.approved_by}）
                </span>
              ) : null}
            </DefinitionItem>
            <DefinitionItem label="最后在线">
              {relativeTime(device.last_seen_at)}
              {device.last_seen_at ? (
                <span className="ml-1 text-xs text-[var(--color-muted-foreground)]">
                  {formatTime(device.last_seen_at)}
                </span>
              ) : null}
            </DefinitionItem>
            <DefinitionItem label="设备公钥 (PEM)">
              {device.public_key_pem ? (
                <details>
                  <summary className="cursor-pointer">已上报（点击展开）</summary>
                  <div className="font-mono-token mt-2 max-h-40 overflow-auto break-all rounded bg-[var(--color-muted)] px-2 py-1.5 text-[11px] leading-relaxed">
                    {device.public_key_pem}
                  </div>
                </details>
              ) : (
                <span className="text-[var(--color-muted-foreground)]">
                  未上报（无法生成设备密钥下发信封）
                </span>
              )}
            </DefinitionItem>
            <DefinitionItem label="备注">{device.note || '—'}</DefinitionItem>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>状态与阶段锁</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-sm text-[var(--color-muted-foreground)]">当前状态</span>
            <Badge variant={meta.variant}>{meta.label}</Badge>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {device.status !== 'active' && (
                <Button
                  size="sm"
                  disabled={statusBusy !== null}
                  onClick={() => void applyStatus('active')}
                >
                  {statusBusy === 'active' ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <CircleCheck className="size-4" />
                  )}
                  批准激活
                </Button>
              )}
              {device.status === 'active' && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={statusBusy !== null}
                  onClick={() => void applyStatus('suspended')}
                >
                  {statusBusy === 'suspended' ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Ban className="size-4" />
                  )}
                  停用
                </Button>
              )}
              {device.status !== 'retired' && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={statusBusy !== null}
                  onClick={() => setConfirmRetire(true)}
                >
                  标记退役
                </Button>
              )}
            </div>
          </div>

          <Separator />

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="stage-lock">阶段锁（stageLockEnabled）</Label>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                开启后设备端按 7 天宽限期 + 三阶段（30/180/360 天）要求仪器激活码；关闭则永久解锁。
              </p>
            </div>
            <div className="flex items-center gap-2">
              {lockBusy ? (
                <Loader2 className="size-4 animate-spin text-[var(--color-muted-foreground)]" />
              ) : null}
              <Switch
                id="stage-lock"
                checked={lockChecked}
                disabled={lockBusy}
                onCheckedChange={(v) => void toggleLock(v)}
              />
              <span className="w-10 text-sm text-[var(--color-muted-foreground)]">
                {lockChecked ? '开启' : '关闭'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Radio className="size-4" />
            阶段锁上报状态
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stageState ? (
            <div className="space-y-4">
              <div className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
                <DefinitionItem label="累计运行时长">
                  {humanizeDuration(stageState.elapsed_ms)}
                </DefinitionItem>
                <DefinitionItem label="上报时间">
                  {formatTime(stageState.reported_at)}
                </DefinitionItem>
                <DefinitionItem label="整体状态">
                  <Badge variant={stageState.fully_activated === 1 ? 'success' : 'warning'}>
                    {stageState.fully_activated === 1 ? '已全部激活' : '未全部激活'}
                  </Badge>
                </DefinitionItem>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  各阶段激活情况
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {STAGES.map((stage) => {
                    const activated = stageFlags[stage]
                    return (
                      <Badge key={stage} variant={activated ? 'success' : 'outline'}>
                        {activated ? <CircleCheck className="mr-1 size-3" /> : null}
                        {STAGE_LABEL[stage]}
                        {activated ? '已激活' : '未激活'}
                      </Badge>
                    )
                  })}
                </div>
                <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                  时间线：7 天宽限期 → 阶段一 30 天 → 阶段二 180 天 → 阶段三 360 天 → 永久解锁。
                  计时由设备端累加（忽略时钟回拨），与服务器时间无关。
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-sm text-[var(--color-muted-foreground)]">
              <Info className="mt-0.5 size-4 shrink-0" />
              设备尚未上报阶段锁状态；设备端联网上报后此处会显示累计运行时长与各阶段激活情况。
            </div>
          )}
        </CardContent>
      </Card>

      {confirmRetire && (
        <ConfirmDialog
          title="标记设备退役"
          description={`退役后 ${device.device_name} 将不再被视为在役设备（状态变为「已退役」）。该操作可在之后重新批准激活。`}
          confirmLabel="确认退役"
          danger
          busy={statusBusy === 'retired'}
          onCancel={() => setConfirmRetire(false)}
          onConfirm={() => void applyStatus('retired')}
        />
      )}
    </div>
  )
}

// ───────────────────────────── 设备密钥 ─────────────────────────────

type SecretAction = 'issue' | 'rotate' | 'reveal' | 'revoke' | 'provision'

function SecretTab({
  device,
  hasSecret,
  onChanged,
}: {
  device: Device
  hasSecret: boolean
  onChanged: () => void
}) {
  const deviceId = device.device_id
  const [busy, setBusy] = useState<SecretAction | null>(null)
  const [keyVersion, setKeyVersion] = useState<number | null>(null)
  const [freshSecret, setFreshSecret] = useState<{
    secretBase32: string
    keyVersion: number
  } | null>(null)
  const [revealed, setRevealed] = useState<{ secretBase32: string; keyVersion: number } | null>(
    null,
  )
  const [envelope, setEnvelope] = useState<{ envelope: string; keyVersion: number } | null>(null)
  const [confirm, setConfirm] = useState<{
    title: string
    description: string
    confirmLabel: string
    danger?: boolean
    run: () => Promise<void>
  } | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)

  const anyBusy = busy !== null

  async function issueSecret(mode: 'issue' | 'rotate') {
    setBusy(mode)
    try {
      const res = await api.issueSecret(deviceId)
      setFreshSecret(res)
      setRevealed(null)
      setEnvelope(null)
      setKeyVersion(res.keyVersion)
      toast.success(
        mode === 'rotate'
          ? `已轮换设备密钥，新版本 v${res.keyVersion}`
          : `已签发设备密钥，版本 v${res.keyVersion}`,
      )
      onChanged()
    } catch (err) {
      toast.error(errorMessage(err, mode === 'rotate' ? '轮换设备密钥失败' : '签发设备密钥失败'))
    } finally {
      setBusy(null)
    }
  }

  async function revealSecret() {
    setBusy('reveal')
    try {
      const res = await api.revealSecret(deviceId)
      setRevealed(res)
      setFreshSecret(null)
      setKeyVersion(res.keyVersion)
      toast.success(`已显示设备密钥明文（版本 v${res.keyVersion}），本次查看已记入操作日志`)
    } catch (err) {
      toast.error(errorMessage(err, '查看设备密钥明文失败'))
    } finally {
      setBusy(null)
    }
  }

  async function revokeSecret() {
    setBusy('revoke')
    try {
      await api.revokeSecret(deviceId)
      setRevealed(null)
      setFreshSecret(null)
      setEnvelope(null)
      setKeyVersion(null)
      toast.success('已吊销设备密钥；设备端将无法再通过校验，需重新签发并下发')
      onChanged()
    } catch (err) {
      toast.error(errorMessage(err, '吊销设备密钥失败'))
    } finally {
      setBusy(null)
    }
  }

  async function provision() {
    setBusy('provision')
    try {
      const res = await api.provision(deviceId)
      setEnvelope(res)
      setKeyVersion(res.keyVersion)
      toast.success(`已生成设备密钥下发信封（版本 v${res.keyVersion}）`)
    } catch (err) {
      toast.error(errorMessage(err, '生成设备密钥下发信封失败'))
    } finally {
      setBusy(null)
    }
  }

  async function runConfirm() {
    if (!confirm) return
    setConfirmBusy(true)
    try {
      await confirm.run()
      setConfirm(null)
    } catch {
      // run() 内部已提示错误，这里只负责关闭对话框
      setConfirm(null)
    } finally {
      setConfirmBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      {!hasSecret && (
        <div className={NOTICE_CLASS}>
          <CircleAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" />
          <div>
            <div className="font-medium text-[var(--color-warning)]">尚未签发设备密钥</div>
            <p className="mt-1 text-[var(--color-muted-foreground)]">
              设备密钥是仪器激活码与维护动态密码的计算基础。设备完成注册并上报公钥后，管理端会自动签发首枚设备密钥；
              若设备尚未上报公钥，请先在设备端完成注册。设备密钥被吊销后不会自动补发，可点击下方「立即签发设备密钥」手动恢复。
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="size-4" />
            设备密钥
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={hasSecret ? 'success' : 'outline'}>
              {hasSecret ? '已签发' : '未签发'}
            </Badge>
            <span className="text-sm text-[var(--color-muted-foreground)]">
              当前设备密钥版本：
              <span className="font-mono-token">
                {keyVersion !== null
                  ? `v${keyVersion}`
                  : hasSecret
                    ? '未知（点击「查看设备密钥明文」后可知）'
                    : '—'}
              </span>
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {!hasSecret ? (
              <Button disabled={anyBusy} onClick={() => void issueSecret('issue')}>
                {busy === 'issue' ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ShieldCheck className="size-4" />
                )}
                立即签发设备密钥
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  disabled={anyBusy}
                  onClick={() =>
                    setConfirm({
                      title: '轮换设备密钥',
                      description:
                        '轮换会立即吊销当前设备密钥并签发新的设备密钥：旧设备密钥马上失效，设备端在收到新的设备密钥（下发信封）之前将无法校验仪器激活码与维护动态密码。',
                      confirmLabel: '确认轮换',
                      danger: true,
                      run: () => issueSecret('rotate'),
                    })
                  }
                >
                  {busy === 'rotate' ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <RotateCcw className="size-4" />
                  )}
                  轮换设备密钥
                </Button>
                <Button variant="outline" disabled={anyBusy} onClick={() => void revealSecret()}>
                  {busy === 'reveal' ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                  查看设备密钥明文
                </Button>
                <Button variant="outline" disabled={anyBusy} onClick={() => void provision()}>
                  {busy === 'provision' ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Send className="size-4" />
                  )}
                  生成设备密钥下发信封
                </Button>
                <Button
                  variant="destructive"
                  disabled={anyBusy}
                  onClick={() =>
                    setConfirm({
                      title: '吊销设备密钥',
                      description:
                        '吊销后该设备将没有任何可用设备密钥：维护动态密码与仪器激活码都无法生成，设备端也会校验失败，直到重新签发并下发。',
                      confirmLabel: '确认吊销',
                      danger: true,
                      run: revokeSecret,
                    })
                  }
                >
                  {busy === 'revoke' ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Ban className="size-4" />
                  )}
                  吊销设备密钥
                </Button>
              </>
            )}
          </div>

          <div className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)] px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-[var(--color-warning)]" />
            <span>
              「查看设备密钥明文」为敏感操作，
              <strong className="text-[var(--color-foreground)]">每次调用都会写入操作日志</strong>
              （secret.reveal）。请在受控环境下操作，避免投屏、录屏或截图泄露设备密钥。
            </span>
          </div>

          {freshSecret && (
            <CopyableBlock
              tone="success"
              title={`新签发的设备密钥明文（版本 v${freshSecret.keyVersion}）`}
              hint="明文只在签发响应中返回；关闭后仅能通过「查看设备密钥明文」再次获取（该操作会被审计）。"
              value={freshSecret.secretBase32}
              action={
                <Button variant="ghost" size="sm" onClick={() => setFreshSecret(null)}>
                  隐藏
                </Button>
              }
            />
          )}

          {revealed && (
            <CopyableBlock
              title={`当前设备密钥明文（版本 v${revealed.keyVersion}）`}
              hint="本次查看已写入操作日志。"
              value={revealed.secretBase32}
              action={
                <Button variant="ghost" size="sm" onClick={() => setRevealed(null)}>
                  隐藏
                </Button>
              }
            />
          )}

          {envelope && (
            <CopyableBlock
              title={`设备密钥下发信封（版本 v${envelope.keyVersion}）`}
              hint="该字符串已用设备公钥（RSA-OAEP）加密，必须交付给对应设备：设备用自身私钥解密后把设备密钥写入安全存储。"
              value={envelope.envelope}
              action={
                <Button variant="ghost" size="sm" onClick={() => setEnvelope(null)}>
                  隐藏
                </Button>
              }
            />
          )}

          {hasSecret && !device.public_key_pem && (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              提示：该设备尚未上报公钥，生成设备密钥下发信封会失败（409）。请在下方「设备公钥」卡片中补录。
            </p>
          )}
        </CardContent>
      </Card>

      <PublicKeyCard device={device} onChanged={onChanged} />

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          description={confirm.description}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger ?? false}
          busy={confirmBusy}
          onCancel={() => {
            if (!confirmBusy) setConfirm(null)
          }}
          onConfirm={() => void runConfirm()}
        />
      )}
    </div>
  )
}

// ───────────────────────────── 仪器激活码 ─────────────────────────────

function ActivationTab({ deviceId, hasSecret }: { deviceId: string; hasSecret: boolean }) {
  const codes = useAsync(() => api.listActivationCodes(deviceId), [deviceId])
  const now = useTicker(1000)
  const [issued, setIssued] = useState<Partial<Record<Stage, ActivationIssue>>>({})
  const [issuing, setIssuing] = useState<Stage | null>(null)
  const [checkStage, setCheckStage] = useState<Stage>(1)
  const [checkCode, setCheckCode] = useState('')
  const [checking, setChecking] = useState(false)
  const [checkResult, setCheckResult] = useState<{
    valid: boolean
    driftWindows: number | null
  } | null>(null)

  async function issueStage(stage: Stage) {
    setIssuing(stage)
    try {
      const res = await api.issueActivation(deviceId, stage)
      setIssued((prev) => ({ ...prev, [stage]: res }))
      toast.success(`${STAGE_LABEL[stage]} 仪器激活码已签发，请在有效期内转达给现场`)
      codes.reload()
    } catch (err) {
      toast.error(errorMessage(err, '签发仪器激活码失败'))
    } finally {
      setIssuing(null)
    }
  }

  async function handleCheck() {
    const code = checkCode.replace(/\s+/g, '')
    if (!/^\d{7}$/.test(code)) {
      toast.error('请输入 7 位数字仪器激活码')
      return
    }
    setChecking(true)
    try {
      const res = await api.checkActivation(deviceId, checkStage, code)
      setCheckResult(res)
      if (res.valid) toast.success('仪器激活码有效')
      else toast.error('仪器激活码无效')
    } catch (err) {
      setCheckResult(null)
      toast.error(errorMessage(err, '校验仪器激活码失败'))
    } finally {
      setChecking(false)
    }
  }

  const rows = codes.data?.rows ?? []

  return (
    <div className="space-y-4">
      <div className={NOTICE_CLASS}>
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" />
        <div>
          <div className="font-medium text-[var(--color-warning)]">仪器激活码每 60 秒变化一次</div>
          <p className="mt-1 text-[var(--color-muted-foreground)]">
            签发的仪器激活码只在其所在时间窗口内有效（校验允许 ±1 个窗口的时钟漂移）。 请签发起 60
            秒内转达给现场操作人员，过期后重新签发即可。
            {hasSecret
              ? null
              : ' 当前设备尚未签发设备密钥。设备上报公钥后会自动签发；若设备密钥已被吊销，请在「设备密钥」页签点击「立即签发设备密钥」。'}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {STAGES.map((stage) => {
          const issue = issued[stage]
          const busy = issuing === stage
          const validSeconds = issue ? Math.max(1, issue.validForSeconds) : 60
          const remaining = issue
            ? Math.max(0, Math.ceil((issue.issuedAt + issue.validForSeconds * 1000 - now) / 1000))
            : 0
          const expired = issue !== undefined && remaining <= 0
          const percent = issue ? Math.min(100, Math.max(0, (remaining / validSeconds) * 100)) : 0

          return (
            <Card key={stage}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <KeyRound className="size-4" />
                    {STAGE_LABEL[stage]}
                  </span>
                  <span className="font-mono-token text-xs text-[var(--color-muted-foreground)]">
                    stage {stage}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {issue ? (
                  <>
                    <div
                      className={cn(
                        'font-mono-token rounded-md bg-[var(--color-muted)] py-4 text-center text-4xl font-semibold',
                        expired
                          ? 'text-[var(--color-muted-foreground)] line-through'
                          : 'text-[var(--color-foreground)]',
                      )}
                    >
                      {formatActivationCode(issue.code)}
                    </div>
                    <div className="space-y-1.5">
                      <div className="countdown-track">
                        <div className="countdown-fill" style={{ width: `${percent}%` }} />
                      </div>
                      <div className="flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
                        <span className={expired ? 'text-[var(--color-destructive)]' : undefined}>
                          {expired ? '已过期，请重新签发' : `剩余有效 ${remaining} 秒`}
                        </span>
                        <span className="font-mono-token">窗口 #{issue.window}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void copyText(issue.code, `${STAGE_LABEL[stage]} 仪器激活码已复制`)
                        }
                      >
                        <Copy className="size-3.5" />
                        复制
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !hasSecret}
                        onClick={() => void issueStage(stage)}
                      >
                        {busy ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="size-3.5" />
                        )}
                        重新签发
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-mono-token rounded-md border border-dashed border-[var(--color-border)] py-5 text-center text-2xl text-[var(--color-muted-foreground)]">
                      - - - - - - -
                    </div>
                    <Button
                      className="w-full"
                      disabled={busy || !hasSecret}
                      onClick={() => void issueStage(stage)}
                    >
                      {busy ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <ShieldCheck className="size-4" />
                      )}
                      签发仪器激活码
                    </Button>
                    {hasSecret ? null : (
                      <p className="text-center text-xs text-[var(--color-muted-foreground)]">
                        需先签发设备密钥
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>校验仪器激活码</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-32 space-y-1.5">
              <Label htmlFor="check-stage">阶段</Label>
              <Select
                value={String(checkStage)}
                onValueChange={(v) => setCheckStage(v === '2' ? 2 : v === '3' ? 3 : 1)}
              >
                <SelectTrigger id="check-stage">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((stage) => (
                    <SelectItem key={stage} value={String(stage)}>
                      {STAGE_LABEL[stage]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[12rem] flex-1 space-y-1.5">
              <Label htmlFor="check-code">仪器激活码</Label>
              <Input
                id="check-code"
                className="font-mono-token"
                placeholder="7 位数字，可含空格"
                maxLength={9}
                value={checkCode}
                onChange={(e) => setCheckCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleCheck()
                }}
              />
            </div>
            <Button disabled={checking || !hasSecret} onClick={() => void handleCheck()}>
              {checking ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ShieldCheck className="size-4" />
              )}
              校验
            </Button>
          </div>

          {checkResult && (
            <div
              className={cn(
                'rounded-md border px-4 py-3 text-sm',
                checkResult.valid
                  ? 'border-[color-mix(in_oklch,var(--color-success)_40%,transparent)] bg-[color-mix(in_oklch,var(--color-success)_10%,transparent)]'
                  : 'border-[color-mix(in_oklch,var(--color-destructive)_40%,transparent)] bg-[color-mix(in_oklch,var(--color-destructive)_10%,transparent)]',
              )}
            >
              <span
                className={
                  checkResult.valid
                    ? 'text-[var(--color-success)]'
                    : 'text-[var(--color-destructive)]'
                }
              >
                {checkResult.valid
                  ? '有效：该仪器激活码校验通过。'
                  : '无效：仪器激活码不正确，或已超出允许的漂移窗口。'}
              </span>
              {checkResult.driftWindows !== null && (
                <span className="ml-1 text-[var(--color-muted-foreground)]">
                  （命中窗口偏移{' '}
                  <span className="font-mono-token">
                    {checkResult.driftWindows > 0
                      ? `+${checkResult.driftWindows}`
                      : checkResult.driftWindows}
                  </span>
                  ：
                  {checkResult.driftWindows === 0
                    ? '当前窗口，设备与服务器时钟一致'
                    : checkResult.driftWindows < 0
                      ? '较早窗口，设备时钟可能慢于服务器'
                      : '较晚窗口，设备时钟可能快于服务器'}
                  ）
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>仪器激活码记录</CardTitle>
        </CardHeader>
        <CardContent>
          {codes.error ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[color-mix(in_oklch,var(--color-destructive)_40%,transparent)] bg-[color-mix(in_oklch,var(--color-destructive)_10%,transparent)] px-4 py-3 text-sm text-[var(--color-destructive)]">
              <span>加载仪器激活码记录失败：{codes.error}</span>
              <Button variant="outline" size="sm" onClick={codes.reload}>
                重试
              </Button>
            </div>
          ) : (
            <div className="rounded-md border border-[var(--color-border)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>阶段</TableHead>
                    <TableHead>仪器激活码</TableHead>
                    <TableHead>签发时间</TableHead>
                    <TableHead>签发人</TableHead>
                    <TableHead>校验时间</TableHead>
                    <TableHead>备注</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.loading && !codes.data ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-10 text-center text-[var(--color-muted-foreground)]"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="size-4 animate-spin" />
                          加载中…
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-10 text-center text-sm text-[var(--color-muted-foreground)]"
                      >
                        暂无签发记录。使用上方卡片签发仪器激活码后，这里会留下记录。
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Badge variant="outline">阶段 {row.stage}</Badge>
                        </TableCell>
                        <TableCell className="font-mono-token">
                          {formatActivationCode(row.code)}
                        </TableCell>
                        <TableCell className="text-[var(--color-muted-foreground)]">
                          {formatTime(row.issued_at)}
                        </TableCell>
                        <TableCell className="text-[var(--color-muted-foreground)]">
                          {row.issued_by || '—'}
                        </TableCell>
                        <TableCell className="text-[var(--color-muted-foreground)]">
                          {row.verified_at ? formatTime(row.verified_at) : '未校验'}
                        </TableCell>
                        <TableCell className="text-[var(--color-muted-foreground)]">
                          {row.note || '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void copyText(row.code, '仪器激活码已复制')}
                            >
                              <Copy className="size-3.5" />
                              复制
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ───────────────────────────── 维护动态密码 ─────────────────────────────

/**
 * 上位机授权页签。
 *
 * 与「仪器激活码」「维护动态密码」的区别，务必在界面上讲清楚：
 *  - 上位机授权码：RSA-2048 对**上位机机器码**签名，决定这台 **PC** 能否运行 desktop 上位机，长期有效；
 *  - 仪器激活码：解锁**仪器**阶段锁，60 秒有效；
 *  - 维护动态密码：出厂/校正入口，60 秒有效。
 */
function LicenseTab({
  deviceId,
  machineCode,
  licenseReady,
  licenseError,
  onMachineCodeSaved,
}: {
  deviceId: string
  machineCode: string | null
  licenseReady: boolean
  licenseError?: string | undefined
  onMachineCodeSaved: () => void
}) {
  const codes = useAsync(() => api.listLicenseCodes(deviceId), [deviceId])
  const [input, setInput] = useState(machineCode ?? '')
  const [note, setNote] = useState('')
  const [issuing, setIssuing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [issued, setIssued] = useState<LicenseCodeRow | null>(null)

  useEffect(() => {
    setInput(machineCode ?? '')
  }, [machineCode])

  const normalized = input.trim().toUpperCase()
  const formatOk = /^[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}$/.test(normalized)

  async function saveMachineCode() {
    setSaving(true)
    try {
      await api.updateDevice(deviceId, { machineCode: normalized })
      toast.success('上位机机器码已保存')
      onMachineCodeSaved()
    } catch (err) {
      toast.error(errorMessage(err, '保存上位机机器码失败'))
    } finally {
      setSaving(false)
    }
  }

  async function issue() {
    setIssuing(true)
    try {
      const res = await api.issueLicense(deviceId, { ...(note ? { note } : {}) })
      setIssued(res)
      toast.success('上位机授权码已签发')
      codes.reload()
    } catch (err) {
      toast.error(errorMessage(err, '签发上位机授权码失败'))
    } finally {
      setIssuing(false)
    }
  }

  const rows = codes.data?.rows ?? []

  return (
    <div className="space-y-4">
      <div className={NOTICE_CLASS}>
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" />
        <div>
          <div className="font-medium text-[var(--color-warning)]">
            上位机授权码与仪器激活码不是一回事
          </div>
          <p className="mt-1 text-[var(--color-muted-foreground)]">
            上位机授权码用 RSA-2048 对<strong>上位机机器码</strong>签名，决定这台电脑能否运行
            desktop 上位机，
            <strong>长期有效、一机一码</strong>；而「仪器激活码」是解锁仪器阶段锁的 7 位数字， 仅 60
            秒有效。上位机机器码由 desktop 端「设置与授权」页显示。
          </p>
        </div>
      </div>

      {!licenseReady && (
        <div
          className={
            'flex items-start gap-3 rounded-md border px-4 py-3 text-sm ' + TONE_CLASS.warning
          }
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" />
          <div>
            <div className="font-medium text-[var(--color-warning)]">
              签发私钥不可用，无法签发上位机授权码
            </div>
            <p className="mt-1 text-[var(--color-muted-foreground)]">
              {licenseError ??
                '请运行 node admin/scripts/gen-license-keypair.mjs 生成授权签发密钥对。'}
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>上位机机器码</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[260px] flex-1 space-y-1.5">
              <Label htmlFor="machine-code">上位机机器码</Label>
              <Input
                id="machine-code"
                value={input}
                onChange={(e) => setInput(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                className="font-mono-token"
              />
            </div>
            <Button variant="outline" onClick={saveMachineCode} disabled={saving || !formatOk}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              保存上位机机器码
            </Button>
          </div>
          {input.trim() !== '' && !formatOk && (
            <p className="text-xs text-[var(--color-destructive)]">
              格式不正确：应为 4 组 4 位十六进制字符（0-9 / A-F）。
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="license-note">备注（可选）</Label>
            <Input
              id="license-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例如：客户名称 / 合同号，便于日后追溯"
            />
          </div>

          <Button onClick={issue} disabled={issuing || !licenseReady || !formatOk}>
            {issuing ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <KeyRound className="size-4" />
            )}
            签发上位机授权码
          </Button>
        </CardContent>
      </Card>

      {issued && (
        <Card className={TONE_CLASS.success}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge variant="success">最新上位机授权码</Badge>
              <span className="font-mono-token text-xs text-[var(--color-muted-foreground)]">
                {issued.machine_code}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea
              readOnly
              value={issued.license_key}
              rows={6}
              className="w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-3 font-mono-token text-xs break-all"
            />
            <CopyButton value={issued.license_key} label="复制上位机授权码" />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>签发历史</CardTitle>
        </CardHeader>
        <CardContent>
          {codes.loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-[var(--color-muted-foreground)]">
              <Loader2 className="size-4 animate-spin" />
              正在加载…
            </div>
          ) : codes.error ? (
            <p className="py-4 text-sm text-[var(--color-destructive)]">{codes.error}</p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-[var(--color-muted-foreground)]">
              尚无签发记录
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>签发时间</TableHead>
                  <TableHead>上位机机器码</TableHead>
                  <TableHead>签发人</TableHead>
                  <TableHead>备注</TableHead>
                  <TableHead>上位机授权码</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs">
                      {formatTime(r.issued_at)}
                    </TableCell>
                    <TableCell className="font-mono-token text-xs">{r.machine_code}</TableCell>
                    <TableCell className="text-xs">{r.issued_by ?? '—'}</TableCell>
                    <TableCell className="text-xs">{r.note ?? '—'}</TableCell>
                    <TableCell>
                      <CopyButton value={r.license_key} label="复制" size="sm" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function DynamicTab({ deviceId, hasSecret }: { deviceId: string; hasSecret: boolean }) {
  const [dyn, setDyn] = useState<DynamicPassword | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const busyRef = useRef(false)
  // 到期时刻锚定在**本地收到响应的时刻** + 服务端给出的剩余秒数，
  // 而不是服务端的绝对时间戳 —— 这样即便本机时钟与服务端有偏差，
  // 也不会出现「一取回就已过期」从而疯狂重取（每次都会写操作日志）的死循环。
  const loadedAtRef = useRef(0)
  const now = useTicker(1000)

  // 每次调用都会写操作日志，因此用 ref 串行化，避免重复请求。
  const load = useCallback(async () => {
    if (!deviceId || busyRef.current) return
    busyRef.current = true
    setLoading(true)
    try {
      const res = await api.dynamicPassword(deviceId)
      loadedAtRef.current = Date.now()
      setDyn(res)
      setError(null)
    } catch (err) {
      setDyn(null)
      setError(errorMessage(err, '获取维护动态密码失败'))
    } finally {
      busyRef.current = false
      setLoading(false)
    }
  }, [deviceId])

  // 打开本标签页时取一次。
  useEffect(() => {
    if (!hasSecret) return
    void load()
  }, [hasSecret, load])

  const expiresAt = dyn ? loadedAtRef.current + dyn.secondsRemaining * 1000 : 0

  // 当前窗口到期后自动取下一窗口。
  useEffect(() => {
    if (!hasSecret || !autoRefresh || !dyn) return
    if (now >= expiresAt) void load()
  }, [now, expiresAt, dyn, autoRefresh, hasSecret, load])

  if (!hasSecret) {
    return (
      <div className={NOTICE_CLASS}>
        <CircleAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" />
        <div>
          <div className="font-medium text-[var(--color-warning)]">
            尚未签发设备密钥，无法生成维护动态密码
          </div>
          <p className="mt-1 text-[var(--color-muted-foreground)]">
            维护动态密码由设备密钥与当前时间窗口推导。设备上报公钥后会自动签发；若设备密钥已被吊销，请到「设备密钥」页签点击「立即签发设备密钥」，再回到此处。
          </p>
        </div>
      </div>
    )
  }

  const step = dyn?.stepSeconds ?? 60
  const remaining = dyn ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0
  const percent = dyn ? Math.min(100, Math.max(0, (remaining / step) * 100)) : 0

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="size-4" />
            维护动态密码
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {error ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[color-mix(in_oklch,var(--color-destructive)_40%,transparent)] bg-[color-mix(in_oklch,var(--color-destructive)_10%,transparent)] px-4 py-3 text-sm text-[var(--color-destructive)]">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={() => void load()}>
                重试
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-6 py-8">
              <div className="font-mono-token text-center text-5xl font-semibold sm:text-6xl">
                {dyn ? formatDynamicCode(dyn.current) : '- - -  - - -'}
              </div>

              <div className="w-full max-w-sm space-y-2">
                <div className="countdown-track">
                  <div className="countdown-fill" style={{ width: `${percent}%` }} />
                </div>
                <div className="flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
                  <span>
                    {dyn ? `${remaining} 秒后自动刷新` : loading ? '获取中…' : '暂无数据'}
                  </span>
                  <span className="font-mono-token">窗口 #{dyn ? dyn.window : '—'}</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!dyn}
                  onClick={() => {
                    if (dyn) void copyText(dyn.current, '维护动态密码已复制')
                  }}
                >
                  <Copy className="size-3.5" />
                  复制当前密码
                </Button>
                <Button variant="ghost" size="sm" disabled={loading} onClick={() => void load()}>
                  {loading ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="size-3.5" />
                  )}
                  立即刷新
                </Button>
              </div>

              <div className="text-center">
                <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  下一窗口密码（提前预览）
                </div>
                <div className="font-mono-token mt-1 text-2xl text-[var(--color-muted-foreground)] opacity-60">
                  {dyn ? formatDynamicCode(dyn.next) : '- - -  - - -'}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-[var(--color-border)] px-4 py-3">
            <div className="space-y-1">
              <Label htmlFor="auto-refresh">自动刷新</Label>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                关闭后需手动点击「立即刷新」。每次获取都会写入操作日志（dynamic_password.view）。
              </p>
            </div>
            <Switch id="auto-refresh" checked={autoRefresh} onCheckedChange={setAutoRefresh} />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ───────────────────────────── 页面 ─────────────────────────────

export function DeviceDetailPage() {
  const params = useParams<{ deviceId: string }>()
  const deviceId = params.deviceId ?? ''
  const [tab, setTab] = useState('overview')

  const detail = useAsync<DeviceDetail>(
    () => (deviceId ? api.getDevice(deviceId) : Promise.reject(new Error('缺少设备 ID'))),
    [deviceId],
  )

  // 后端健康状态：用于提示「签发私钥是否就绪」，决定上位机授权码/签名页签能否操作
  const health = useAsync(() => api.health(), [])

  if (detail.loading && !detail.data) {
    return (
      <div className="grid place-items-center py-24 text-[var(--color-muted-foreground)]">
        <span className="inline-flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" />
          加载设备信息…
        </span>
      </div>
    )
  }

  if (!detail.data) {
    return (
      <div className="space-y-4">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 text-[var(--color-muted-foreground)]"
        >
          <Link to="/devices">
            <ArrowLeft className="size-4" />
            返回设备列表
          </Link>
        </Button>
        <Card>
          <CardContent className="space-y-3 pt-5">
            <div className="flex items-start gap-2 text-sm text-[var(--color-destructive)]">
              <CircleAlert className="mt-0.5 size-4 shrink-0" />
              <span>
                无法加载设备 {deviceId || '（未指定）'}：{detail.error ?? '设备不存在'}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={detail.reload}>
                <RefreshCw className="size-4" />
                重试
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/devices">返回列表</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const data = detail.data
  const device = data.device
  const meta = STATUS_META[device.status]

  return (
    <div className="space-y-6">
      <div>
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 mb-2 text-[var(--color-muted-foreground)]"
        >
          <Link to="/devices">
            <ArrowLeft className="size-4" />
            返回设备列表
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">{device.device_name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[var(--color-muted-foreground)]">
              <span className="font-mono-token">{device.device_id}</span>
              <span>·</span>
              <span>{device.device_type}</span>
              {device.model ? (
                <>
                  <span>·</span>
                  <span>{device.model}</span>
                </>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={meta.variant}>{meta.label}</Badge>
            <Badge variant={data.hasSecret ? 'success' : 'outline'}>
              {data.hasSecret ? '设备密钥已签发' : '设备密钥未签发'}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              disabled={detail.loading}
              onClick={detail.reload}
              title="重新加载设备信息"
            >
              <RefreshCw className={cn('size-4', detail.loading && 'animate-spin')} />
              刷新
            </Button>
          </div>
        </div>
        {detail.error ? (
          <p className="mt-2 text-xs text-[var(--color-destructive)]">
            上次刷新失败：{detail.error}
          </p>
        ) : null}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">设备概览</TabsTrigger>
          <TabsTrigger value="registration">注册档案</TabsTrigger>
          <TabsTrigger value="secret">设备密钥</TabsTrigger>
          <TabsTrigger value="activation">仪器激活码</TabsTrigger>
          <TabsTrigger value="license">上位机授权码</TabsTrigger>
          <TabsTrigger value="dynamic">维护动态密码</TabsTrigger>
        </TabsList>

        <TabsContent value="registration">
          <RegistrationTab device={device} onChanged={() => detail.reload()} />
        </TabsContent>

        <TabsContent value="overview">
          <OverviewTab device={device} stageState={data.stageState} onChanged={detail.reload} />
        </TabsContent>

        <TabsContent value="secret">
          <SecretTab device={device} hasSecret={data.hasSecret} onChanged={detail.reload} />
        </TabsContent>

        <TabsContent value="activation">
          <ActivationTab deviceId={device.device_id} hasSecret={data.hasSecret} />
        </TabsContent>

        <TabsContent value="license">
          <LicenseTab
            deviceId={deviceId}
            machineCode={device?.machine_code ?? null}
            // 必须显式要求 === true：health 尚未加载时 `!== false` 会误判为已就绪，
            // 让用户在私钥缺失时点下去才发现 503
            licenseReady={health.data?.licenseKeyConfigured === true}
            licenseError={health.data?.licenseKeyError}
            onMachineCodeSaved={() => detail.reload()}
          />
        </TabsContent>

        <TabsContent value="dynamic">
          <DynamicTab deviceId={device.device_id} hasSecret={data.hasSecret} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
